/* Wishlist session + auth helpers.
 *
 * Bridges the storefront's idea of "who am I" to the Worker:
 *
 *   1. Logged-in customer: window.Shopify.customer.id is populated by
 *      layout/theme.liquid's customer-bridge script. The Worker reads
 *      logged_in_customer_id from the App Proxy URL query string —
 *      we don't need to send any auth header.
 *
 *   2. Guest: a long-lived session_token in localStorage. Sent as the
 *      `X-Wishlist-Session-Token` request header. Created lazily on
 *      first use; persists across sessions until clearing localStorage.
 *
 * Also exposes `migrateWishlistOnLogin` — runs once on every page load
 * when a logged-in customer is detected AND a guest session_token
 * exists. Re-keys any guest wishes onto the customer, then clears the
 * local session_token so subsequent requests use the customer-id path.
 */
(function () {
  if (window.__themeWishlistSessionInit) return;
  window.__themeWishlistSessionInit = true;

  var SESSION_TOKEN_KEY = 'theme:wishlist:session_token';
  var MIGRATION_DONE_KEY = 'theme:wishlist:migrated_for_customer';

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC4122-ish fallback for older browsers.
    var d = Date.now();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function getSessionToken() {
    try {
      var token = localStorage.getItem(SESSION_TOKEN_KEY);
      if (!token) {
        token = uuid();
        localStorage.setItem(SESSION_TOKEN_KEY, token);
      }
      return token;
    } catch (e) {
      // Private mode / disabled storage — fall back to in-memory token.
      if (!window.__themeWishlistInMemoryToken) {
        window.__themeWishlistInMemoryToken = uuid();
      }
      return window.__themeWishlistInMemoryToken;
    }
  }

  function clearSessionToken() {
    try { localStorage.removeItem(SESSION_TOKEN_KEY); } catch (e) { /* ignore */ }
    delete window.__themeWishlistInMemoryToken;
  }

  function isLoggedIn() {
    return !!(window.Shopify && window.Shopify.customer && window.Shopify.customer.id);
  }

  function getCustomerId() {
    if (!isLoggedIn()) return null;
    return window.Shopify.customer.id;
  }

  /** Header set used by every /apps/api/wishlist/* request.
   *  - Customer path: just Content-Type. App Proxy auto-injects
   *    logged_in_customer_id from Shopify's session cookie.
   *  - Guest path: X-Wishlist-Session-Token. */
  function getAuthHeaders() {
    var headers = { 'Content-Type': 'application/json' };
    if (!isLoggedIn()) {
      headers['X-Wishlist-Session-Token'] = getSessionToken();
    }
    return headers;
  }

  function getApiBase() {
    return window.__wishlistApiBase || '/apps/api/wishlist';
  }

  /** Migrate guest wishes onto the customer after login. Idempotent —
   *  guarded against re-running for the same customer via a localStorage
   *  flag. The Worker is also idempotent on the SQL side, but the flag
   *  saves a network call on every page load post-migration. */
  async function migrateWishlistOnLogin() {
    if (!isLoggedIn()) return;
    var customerId = getCustomerId();
    var doneFor;
    try { doneFor = localStorage.getItem(MIGRATION_DONE_KEY); } catch (e) { doneFor = null; }
    if (doneFor === String(customerId)) return;

    var token;
    try { token = localStorage.getItem(SESSION_TOKEN_KEY); } catch (e) { token = null; }
    if (!token) {
      // No guest token to migrate, but mark done so we don't retry.
      try { localStorage.setItem(MIGRATION_DONE_KEY, String(customerId)); } catch (e) { /* ignore */ }
      return;
    }

    try {
      var resp = await fetch(getApiBase() + '/migrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: token }),
      });
      if (resp.ok) {
        var data = await resp.json();
        console.log('[wishlist] migrated', data.migrated, 'guest wishes,', data.already_wished, 'duplicates');
        clearSessionToken();
        try { localStorage.setItem(MIGRATION_DONE_KEY, String(customerId)); } catch (e) { /* ignore */ }
        // Notify the rest of the app so it re-reads the wishlist state
        // under the new customer auth.
        document.dispatchEvent(new CustomEvent('wishlist:migrated', { detail: data }));
      } else {
        console.warn('[wishlist] migration failed:', resp.status);
      }
    } catch (err) {
      console.warn('[wishlist] migration error:', err);
      // Will retry on next page load.
    }
  }

  window.ThemeWishlistSession = {
    getSessionToken: getSessionToken,
    clearSessionToken: clearSessionToken,
    isLoggedIn: isLoggedIn,
    getCustomerId: getCustomerId,
    getAuthHeaders: getAuthHeaders,
    getApiBase: getApiBase,
    migrateWishlistOnLogin: migrateWishlistOnLogin,
  };

  // Auto-run migration on every page load when logged in. Cheap — the
  // localStorage flag short-circuits after first success.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', migrateWishlistOnLogin);
  } else {
    migrateWishlistOnLogin();
  }
})();
